import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../lib/axios';
import Header from '../components/layout/Header';

const TYPES = {
    text:     'Texto',
    number:   'Número',
    date:     'Fecha',
    select:   'Lista de opciones',
    checkbox: 'Sí / No',
};

const ENTITIES = {
    contact: 'Contactos',
    deal:    'Negocios',
};

const EMPTY = { entity_type: 'contact', name: '', type: 'text', options: '' };

/**
 * Gestión de los campos personalizados del tenant.
 *
 * La API existía desde antes pero no había pantalla: los campos sólo se podían
 * crear por consola o, desde hace poco, al mapear un formulario. Sin un lugar
 * donde verlos, era imposible saber qué campos tenía el CRM ni corregirles el
 * nombre.
 */
export default function CustomFieldsPage() {
    const qc = useQueryClient();
    const [draft, setDraft] = useState(EMPTY);
    const [editing, setEditing] = useState(null);

    const { data: resp, isLoading } = useQuery({
        queryKey: ['custom-fields'],
        queryFn: () => axios.get('/api/custom-fields'),
    });
    // AppServiceProvider llama a JsonResource::withoutWrapping(), así que esta
    // colección vuelve como array plano y no envuelta en `data`. Se contemplan
    // las dos formas por si esa configuración global cambia.
    const fields = Array.isArray(resp?.data) ? resp.data : (resp?.data?.data || []);

    const done = () => {
        qc.invalidateQueries({ queryKey: ['custom-fields'] });
        setDraft(EMPTY);
        setEditing(null);
    };

    // La clave se deriva del nombre y no se vuelve a tocar al renombrar: es lo
    // que enlaza el campo con los valores ya guardados y con el mapeo de los
    // formularios, así que cambiarla dejaría huérfano todo lo cargado.
    const keyFrom = (name) =>
        name.toLowerCase()
            .normalize('NFD').replace(/\p{Diacritic}/gu, '')
            .replace(/[^a-z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '')
            .slice(0, 60);

    const save = useMutation({
        mutationFn: () => {
            const payload = {
                entity_type: draft.entity_type,
                name: draft.name.trim(),
                type: draft.type,
                options: draft.type === 'select'
                    ? draft.options.split(',').map((o) => o.trim()).filter(Boolean)
                    : null,
            };
            return editing
                ? axios.put(`/api/custom-fields/${editing}`, payload)
                : axios.post('/api/custom-fields', { ...payload, key: keyFrom(payload.name) });
        },
        onSuccess: done,
    });

    const remove = useMutation({
        mutationFn: (id) => axios.delete(`/api/custom-fields/${id}`),
        onSuccess: done,
    });

    const startEdit = (f) => {
        setEditing(f.id);
        setDraft({
            entity_type: f.entity_type,
            name: f.name,
            type: f.type,
            options: (f.options || []).join(', '),
        });
    };

    const grouped = Object.keys(ENTITIES).map((entity) => ({
        entity,
        items: fields.filter((f) => f.entity_type === entity),
    }));

    return (
        <div className="flex-1 flex flex-col overflow-hidden">
            <Header title="Campos personalizados" />

            <div className="flex-1 overflow-y-auto p-6 max-w-4xl w-full mx-auto space-y-6">
                <p className="text-sm text-gray-500">
                    Datos propios de tu operación que no vienen de fábrica en el CRM. Aparecen en la
                    ficha de cada contacto y se pueden completar desde un formulario web.
                </p>

                {/* Alta / edición */}
                <section className="bg-white dark:bg-[#1a100c] border border-gray-200 dark:border-gray-800 rounded-xl p-4">
                    <h2 className="text-sm font-semibold dark:text-white mb-3">
                        {editing ? 'Editar campo' : 'Nuevo campo'}
                    </h2>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Nombre</label>
                            <input
                                value={draft.name}
                                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                                placeholder="Ej. Presupuesto disponible"
                                className="w-full text-sm p-2.5 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Tipo</label>
                            <select
                                value={draft.type}
                                onChange={(e) => setDraft({ ...draft, type: e.target.value })}
                                className="w-full text-sm p-2.5 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white"
                            >
                                {Object.entries(TYPES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Se aplica a</label>
                            <select
                                value={draft.entity_type}
                                disabled={Boolean(editing)}
                                onChange={(e) => setDraft({ ...draft, entity_type: e.target.value })}
                                className="w-full text-sm p-2.5 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white disabled:opacity-60"
                            >
                                {Object.entries(ENTITIES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                        </div>
                    </div>

                    {draft.type === 'select' && (
                        <div className="mt-3">
                            <label className="text-xs text-gray-500 mb-1 block">Opciones, separadas por coma</label>
                            <input
                                value={draft.options}
                                onChange={(e) => setDraft({ ...draft, options: e.target.value })}
                                placeholder="Inmediato, En 3 meses, Sin definir"
                                className="w-full text-sm p-2.5 border border-gray-200 dark:border-gray-700 rounded-lg dark:bg-slate-800 dark:text-white"
                            />
                        </div>
                    )}

                    <div className="mt-3 flex gap-2">
                        <button
                            onClick={() => save.mutate()}
                            disabled={!draft.name.trim() || save.isPending}
                            className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium disabled:opacity-40"
                        >
                            {editing ? 'Guardar cambios' : 'Crear campo'}
                        </button>
                        {editing && (
                            <button onClick={done} className="px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm dark:text-white">
                                Cancelar
                            </button>
                        )}
                    </div>
                </section>

                {/* Listado */}
                {isLoading ? (
                    <p className="text-sm text-gray-400">Cargando…</p>
                ) : fields.length === 0 ? (
                    <div className="text-center py-10 border border-dashed border-gray-300 dark:border-gray-700 rounded-xl">
                        <span className="material-symbols-outlined text-4xl text-gray-300 dark:text-gray-700">tune</span>
                        <p className="text-sm font-semibold dark:text-white mt-2">Todavía no hay campos personalizados</p>
                    </div>
                ) : (
                    grouped.filter((g) => g.items.length > 0).map((g) => (
                        <section key={g.entity} className="bg-white dark:bg-[#1a100c] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden">
                            <h2 className="px-4 py-2.5 text-sm font-semibold dark:text-white border-b border-gray-100 dark:border-gray-800">
                                {ENTITIES[g.entity]}
                            </h2>
                            <div className="divide-y divide-gray-100 dark:divide-gray-800">
                                {g.items.map((f) => (
                                    <div key={f.id} className="px-4 py-3 flex items-center gap-3">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium dark:text-white truncate">{f.name}</p>
                                            <p className="text-xs text-gray-400">
                                                {TYPES[f.type] || f.type}
                                                <span className="mx-1.5">·</span>
                                                <code className="text-[11px]">{f.key}</code>
                                                {f.type === 'select' && (f.options || []).length > 0 && (
                                                    <span className="ml-1.5">({f.options.join(', ')})</span>
                                                )}
                                            </p>
                                        </div>
                                        <button onClick={() => startEdit(f)} title="Editar"
                                            className="shrink-0 text-gray-400 hover:text-primary">
                                            <span className="material-symbols-outlined text-lg">edit</span>
                                        </button>
                                        <button
                                            onClick={() => {
                                                // Borrar el campo borra también todos sus valores
                                                // cargados: conviene que quede claro antes.
                                                if (confirm(`Se eliminará «${f.name}» y todos los datos guardados en él, en todos los contactos. ¿Seguir?`)) {
                                                    remove.mutate(f.id);
                                                }
                                            }}
                                            title="Eliminar"
                                            className="shrink-0 text-gray-400 hover:text-red-500"
                                        >
                                            <span className="material-symbols-outlined text-lg">delete</span>
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </section>
                    ))
                )}
            </div>
        </div>
    );
}
